// PREZENCE — App Router (UPDATED: added admin leaves + activity routes)
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

import LoginPage          from './pages/LoginPage';
import AppLayout          from './components/shared/AppLayout';
import AdminDashboard     from './pages/admin/AdminDashboard';
import EmployeeList       from './pages/admin/EmployeeList';
import ScreenshotView     from './pages/admin/ScreenshotView';
import AdminReports       from './pages/admin/AdminReports';
import Analytics          from './pages/admin/Analytics';
import AuditLog           from './pages/admin/AuditLog';
import ConsentReport      from './pages/admin/ConsentReport';
import Transparency       from './pages/shared/Transparency';
import AdminLeaves        from './pages/admin/AdminLeaves';
import AdminActivity      from './pages/admin/AdminActivity';
import AddEmployee        from './pages/admin/AddEmployee';
import Departments        from './pages/admin/Departments';
import EmployeeDashboard  from './pages/employee/EmployeeDashboard';
import MyAttendance       from './pages/employee/MyAttendance';
import MyLeaves           from './pages/employee/MyLeaves';
import MyActivity         from './pages/employee/MyActivity';
import MyReport           from './pages/employee/MyReport';
import HRDashboard        from './pages/hr/HRDashboard';
import LeaveApprovals     from './pages/hr/LeaveApprovals';
import ProfilePage        from './pages/ProfilePage';
import NotFound           from './pages/NotFound';

const getHomeByRole = r => r==='admin'||r==='superadmin' ? '/admin' : r==='hr' ? '/hr' : '/employee';

const PrivateRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/unauthorized" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to={getHomeByRole(user.role)} replace />;
  return children;
};

const FullPageLoader = () => (
  <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#05090f' }}>
    <div style={{ textAlign:'center' }}>
      <div className="spinner" style={{ width:32, height:32, margin:'0 auto 16px' }} />
      <p style={{ color:'#475569', fontFamily:'JetBrains Mono', fontSize:12 }}>Loading Prezence...</p>
    </div>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* Admin routes */}
      <Route path="/admin" element={<PrivateRoute roles={['admin','superadmin']}><AppLayout /></PrivateRoute>}>
        <Route index                  element={<AdminDashboard />} />
        <Route path="employees"       element={<EmployeeList />} />
        <Route path="employees/add"   element={<AddEmployee />} />
        <Route path="departments"     element={<Departments />} />
        <Route path="screenshots"     element={<ScreenshotView />} />
        <Route path="leaves"          element={<AdminLeaves />} />
        <Route path="activity"        element={<AdminActivity />} />
        <Route path="reports"         element={<AdminReports />} />
        <Route path="analytics"       element={<Analytics />} />
        <Route path="audit"           element={<AuditLog />} />
        <Route path="consent"         element={<ConsentReport />} />
        <Route path="transparency"    element={<Transparency />} />
        <Route path="profile"         element={<ProfilePage />} />
      </Route>

      {/* Employee routes */}
      <Route path="/employee" element={<PrivateRoute roles={['employee']}><AppLayout /></PrivateRoute>}>
        <Route index              element={<EmployeeDashboard />} />
        <Route path="attendance"  element={<MyAttendance />} />
        <Route path="leaves"      element={<MyLeaves />} />
        <Route path="activity"    element={<MyActivity />} />
        <Route path="report"      element={<MyReport />} />
        <Route path="profile"     element={<ProfilePage />} />
        <Route path="transparency" element={<Transparency />} />
      </Route>

      {/* HR routes */}
      <Route path="/hr" element={<PrivateRoute roles={['hr']}><AppLayout /></PrivateRoute>}>
        <Route index              element={<HRDashboard />} />
        <Route path="leaves"      element={<LeaveApprovals />} />
        <Route path="employees"   element={<EmployeeList />} />
        <Route path="departments" element={<Departments />} />
        <Route path="reports"     element={<AdminReports />} />
        <Route path="analytics"   element={<Analytics />} />
        <Route path="audit"       element={<AuditLog />} />
        <Route path="consent"     element={<ConsentReport />} />
        <Route path="transparency" element={<Transparency />} />
        <Route path="profile"     element={<ProfilePage />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" toastOptions={{
          duration: 3500,
          style: { background:'#0d1520', color:'#f0f6ff', border:'1px solid rgba(56,189,248,0.15)', fontFamily:"'DM Sans', sans-serif", fontSize:'13px' },
          success: { iconTheme: { primary:'#22c55e', secondary:'#0d1520' } },
          error:   { iconTheme: { primary:'#ef4444', secondary:'#0d1520' } },
        }} />
      </BrowserRouter>
    </AuthProvider>
  );
}
